import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import {
  Edit,
  Trash2,
  PlusCircle,
  ChevronUp,
  ChevronDown,
  List,
  Type,
  Tag,
  Hash,
  FileText,
  BarChart2,
  CheckCircle,
} from "react-feather";
import Loader from "../../components/common/Loader";
import api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { useLocation } from "react-router-dom";

const QuestionManager = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [highlightQuestionId, setHighlightQuestionId] = useState(null);
  const location = useLocation();

  const [formData, setFormData] = useState({
    questionId: "",
    questionText: "",
    questionType: "points",
    category: "academic",
    order: 0,
    options: [{ text: "", value: "", points: 0 }],
  });
  const { adminData } = useAuth();
  const isSuperAdmin = adminData?.role === "super_admin";

  // Updated category mapping based on your seed data
  const categoryColors = {
    basic: "primary",
    academic: "info",
    language: "warning",
    employment: "secondary",
    financial: "success",
    Immigration: "dark",
    visa: "danger",
    documentation: "warning",
    accommodation: "primary",
  };

  const typeColors = {
    points: "success",
    no_points: "secondary",
    conditional: "warning",
  };

  // Get all unique categories from questions
  const getAllCategories = () => {
    const categories = new Set();
    questions.forEach((q) => categories.add(q.category));
    return Array.from(categories).sort();
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  // If navigated from global search with a specific questionId, remember it
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qId = params.get("questionId") || params.get("q");
    if (qId) {
      setHighlightQuestionId(qId);
    } else {
      setHighlightQuestionId(null);
    }
  }, [location.search]);

  const fetchQuestions = async () => {
    try {
      const response = await api.get("/questions/admin");
      if (response.data.success) {
        // Sort questions by order
        const sortedQuestions = response.data.questions.sort(
          (a, b) => a.order - b.order,
        );
        setQuestions(sortedQuestions);
      }
    } catch (error) {
      setError("Failed to fetch questions");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!isSuperAdmin) {
        setError("Only super admins can create or update questions");
        return;
      }

      if (editingQuestion) {
        await api.put(`/questions/admin/${editingQuestion._id}`, formData);
        setSuccess("Question updated successfully");
      } else {
        await api.post("/questions/admin", formData);
        setSuccess("Question created successfully");
      }

      fetchQuestions();
      handleCloseModal();
    } catch (error) {
      setError("Operation failed");
    }
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setFormData({
      questionId: question.questionId,
      questionText: question.questionText,
      questionType: question.questionType,
      category: question.category,
      order: question.order,
      options: question.options,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this question?")) {
      try {
        await api.delete(`/questions/admin/${id}`);
        setSuccess("Question deleted successfully");
        fetchQuestions();
      } catch (error) {
        setError("Failed to delete question");
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingQuestion(null);
    setFormData({
      questionId: "",
      questionText: "",
      questionType: "points",
      category: "academic",
      order: 0,
      options: [{ text: "", value: "", points: 0 }],
    });
  };

  const addOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, { text: "", value: "", points: 0 }],
    });
  };

  const removeOption = (index) => {
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({ ...formData, options: newOptions });
  };

  const updateOption = (index, field, value) => {
    const newOptions = [...formData.options];
    newOptions[index][field] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const filteredQuestions =
    activeTab === "all"
      ? questions
      : questions.filter((q) => q.category === activeTab);

  // Get category count statistics
  const getCategoryStats = () => {
    const stats = {};
    questions.forEach((question) => {
      stats[question.category] = (stats[question.category] || 0) + 1;
    });
    return stats;
  };

  const categoryStats = getCategoryStats();

  if (loading) return <Loader />;

  return (
    <div className="question-manager">
      <Card className="border-0 mb-4">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1 fw-bold text-gradient">
                <FileText size={28} className="me-2" />
                Question Management
              </h2>
              <p className="text-muted mb-0">
                Manage and organize all assessment questions
              </p>
            </div>
            {isSuperAdmin && (
              <Button
                variant="primary"
                onClick={() => setShowModal(true)}
                className="d-flex align-items-center gap-2 px-4 py-2"
              >
                <PlusCircle size={18} />
                Add New Question
              </Button>
            )}
          </div>

          {error && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => setError("")}
              className="border-0 shadow-sm"
            >
              <div className="d-flex align-items-center">
                <div className="alert-icon bg-danger rounded-circle d-flex align-items-center justify-content-center me-3">
                  <i className="bi bi-exclamation-triangle-fill text-white"></i>
                </div>
                <div>{error}</div>
              </div>
            </Alert>
          )}

          {success && (
            <Alert
              variant="success"
              dismissible
              onClose={() => setSuccess("")}
              className="border-0 shadow-sm"
            >
              <div className="d-flex align-items-center">
                <div className="alert-icon bg-success rounded-circle d-flex align-items-center justify-content-center me-3">
                  <CheckCircle size={18} className="text-white" />
                </div>
                <div>{success}</div>
              </div>
            </Alert>
          )}

          {/* Category Filter Tabs */}
          <div className="category-tabs mb-4">
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant={activeTab === "all" ? "primary" : "outline-primary"}
                onClick={() => setActiveTab("all")}
                className="rounded-pill px-3"
              >
                All Questions ({questions.length})
              </Button>
              {getAllCategories().map((category) => (
                <Button
                  key={category}
                  variant={
                    activeTab === category
                      ? categoryColors[category] || "primary"
                      : `outline-${categoryColors[category] || "primary"}`
                  }
                  onClick={() => setActiveTab(category)}
                  className="rounded-pill px-3 text-capitalize d-flex align-items-center gap-2"
                >
                  <Tag size={14} />
                  {category} ({categoryStats[category] || 0})
                </Button>
              ))}
            </div>
          </div>

          {/* Stats Cards */}
          <Row className="mb-4 g-3">
            <Col md={2}>
              <Card className="stat-card border-0 shadow-sm h-100">
                <Card.Body className="d-flex align-items-center">
                  <div className="stat-icon bg-primary bg-opacity-10 text-primary rounded-circle p-3 me-3">
                    <BarChart2 size={24} />
                  </div>
                  <div>
                    <h3 className="mb-0 fw-bold">{questions.length}</h3>
                    <p className="text-muted mb-0">Total Questions</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            {getAllCategories()
              .slice(0, 9)
              .map((category) => (
                <Col md={2} key={category}>
                  <Card className="stat-card border-0 shadow-sm h-100">
                    <Card.Body className="d-flex align-items-center">
                      <div
                        className={`stat-icon bg-${categoryColors[category] || "primary"} bg-opacity-10 text-${categoryColors[category] || "primary"} rounded-circle p-3 me-3`}
                      >
                        <Tag size={24} />
                      </div>
                      <div>
                        <h3 className="mb-0 fw-bold">
                          {categoryStats[category] || 0}
                        </h3>
                        <p className="text-muted mb-0 text-capitalize">
                          {category}
                        </p>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
          </Row>

          {/* Questions Table */}
          <Card className="border-0">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-4">
                        <div className="d-flex align-items-center gap-2">
                          <Hash size={16} />
                          Order
                        </div>
                      </th>
                      <th>ID</th>
                      <th>Question Text</th>
                      <th>Type</th>
                      <th>Category</th>
                      <th>Options</th>
                      <th className="text-end pe-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredQuestions.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          <div className="text-muted">
                            No questions found for this category.
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredQuestions.map((question) => {
                        const isHighlighted =
                          highlightQuestionId &&
                          (question._id === highlightQuestionId ||
                            question.questionId === highlightQuestionId);
                        return (
                          <tr
                            key={question._id}
                            className="align-middle"
                            style={
                              isHighlighted
                                ? {
                                    backgroundColor: "rgba(59,130,246,0.08)",
                                    boxShadow:
                                      "inset 0 0 0 1px rgba(59,130,246,0.35)",
                                  }
                                : undefined
                            }
                          >
                          <td className="ps-4">
                            <div className="d-flex align-items-center gap-1">
                              <span className="fw-bold">{question.order}</span>
                              <div className="d-flex flex-column">
                                <ChevronUp size={12} className="text-muted" />
                                <ChevronDown size={12} className="text-muted" />
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="fw-semibold text-primary">
                              {question.questionId}
                            </div>
                          </td>
                          <td>
                            <div className="question-text">
                              <div className="fw-medium">
                                {question.questionText}
                              </div>
                              <small className="text-muted">
                                Created{" "}
                                {new Date(
                                  question.createdAt,
                                ).toLocaleDateString()}
                              </small>
                            </div>
                          </td>
                          <td>
                            <Badge
                              bg={
                                typeColors[question.questionType] || "secondary"
                              }
                              className="text-capitalize"
                            >
                              {question.questionType.replace("_", " ")}
                            </Badge>
                          </td>
                          <td>
                            <Badge
                              bg={
                                categoryColors[question.category] || "primary"
                              }
                              className="text-capitalize"
                            >
                              {question.category}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex flex-column">
                              <span className="fw-semibold">
                                {question.options.length} options
                              </span>
                              <small className="text-muted">
                                Max points:{" "}
                                {Math.max(
                                  ...question.options.map((o) => o.points),
                                )}
                              </small>
                            </div>
                          </td>
                          <td className="text-end pe-4">
                            <div className="d-flex justify-content-end gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="d-flex align-items-center gap-1 px-3"
                                onClick={() => handleEdit(question)}
                                disabled={!isSuperAdmin}
                                title={
                                  !isSuperAdmin
                                    ? "Only super admins can edit questions"
                                    : "Edit"
                                }
                              >
                                <Edit size={14} />
                                Edit
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="d-flex align-items-center gap-1 px-3"
                                onClick={() => handleDelete(question._id)}
                                disabled={!isSuperAdmin}
                                title={
                                  !isSuperAdmin
                                    ? "Only super admins can delete questions"
                                    : "Delete"
                                }
                              >
                                <Trash2 size={14} />
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        centered
        className="question-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">
            {editingQuestion ? (
              <div className="d-flex align-items-center gap-2">
                <Edit size={20} />
                Edit Question
              </div>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <PlusCircle size={20} />
                Add New Question
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="pt-0">
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <h6 className="fw-semibold mb-3 text-primary">
                  <Type size={16} className="me-2" />
                  Basic Information
                </h6>

                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">Question ID</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.questionId}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            questionId: e.target.value,
                          })
                        }
                        required
                        className="py-2"
                        placeholder="e.g., q1, q2"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">
                        Display Order
                      </Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.order}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            order: parseInt(e.target.value),
                          })
                        }
                        required
                        className="py-2"
                        min="1"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Question Text</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.questionText}
                    onChange={(e) =>
                      setFormData({ ...formData, questionText: e.target.value })
                    }
                    required
                    className="py-2"
                    placeholder="Enter the question text here..."
                  />
                </Form.Group>

                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">
                        Question Type
                      </Form.Label>
                      <Form.Select
                        value={formData.questionType}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            questionType: e.target.value,
                          })
                        }
                        className="py-2"
                      >
                        <option value="points">Points Based</option>
                        <option value="no_points">No Points</option>
                        <option value="conditional">Conditional</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">Category</Form.Label>
                      <Form.Select
                        value={formData.category}
                        onChange={(e) =>
                          setFormData({ ...formData, category: e.target.value })
                        }
                        className="py-2"
                      >
                        <option value="basic">Basic</option>
                        <option value="academic">Academic</option>
                        <option value="language">Language</option>
                        <option value="employment">Employment</option>
                        <option value="financial">Financial</option>
                        <option value="Immigration">Immigration</option>
                        <option value="visa">Visa</option>
                        <option value="documentation">Documentation</option>
                        <option value="accommodation">Accommodation</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-semibold mb-0 text-primary">
                    <List size={16} className="me-2" />
                    Answer Options
                  </h6>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={addOption}
                    className="d-flex align-items-center gap-1"
                  >
                    <PlusCircle size={14} />
                    Add Option
                  </Button>
                </div>

                <div className="options-container">
                  {formData.options.map((option, index) => (
                    <div
                      key={index}
                      className="option-card border rounded p-3 mb-3"
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="option-number bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: "24px", height: "24px" }}
                          >
                            <span className="small">{index + 1}</span>
                          </div>
                          <span className="fw-medium">Option {index + 1}</span>
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeOption(index)}
                          disabled={formData.options.length === 1}
                          className="px-2 py-1"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                      <Row className="g-3">
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label className="small fw-medium">
                              Option Text
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value={option.text}
                              onChange={(e) =>
                                updateOption(index, "text", e.target.value)
                              }
                              required
                              className="py-2"
                              placeholder="Enter option text"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label className="small fw-medium">
                              Value
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value={option.value}
                              onChange={(e) =>
                                updateOption(index, "value", e.target.value)
                              }
                              required
                              className="py-2"
                              placeholder="Enter value"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label className="small fw-medium">
                              Points
                            </Form.Label>
                            <Form.Control
                              type="number"
                              value={option.points}
                              onChange={(e) =>
                                updateOption(
                                  index,
                                  "points",
                                  parseInt(e.target.value),
                                )
                              }
                              required
                              className="py-2"
                              min="0"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="light" onClick={handleCloseModal} className="px-4">
              Cancel
            </Button>
            <Button variant="primary" type="submit" className="px-4">
              {editingQuestion ? "Update Question" : "Create Question"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default QuestionManager;
